<template>
  <div class="list-root">
    <div class="head">
      <div class="title ellipsis">{{ $t('correlationsPage.correlationGroupList.title') }}</div>
      <div class="edit">
        <div v-if="!isEditing" @click="isEditing = true">{{ $t('correlationsPage.correlationGroupList.sortButton') }}</div>
        <div v-else @click="saveOrder">{{ $t('common.save') }}</div>
      </div>
    </div>
    <sortable-list
      class="list-container"
      :items.sync="correlationGroupList"
      keyAttr="correlationGroupId"
      :disabled="!isEditing"
      :extra="isEditing"
    >
      <template v-slot:default="{ item, extra }">
        <list-item
          icon="none"
          :label="item.name"
          :to="{
            name: 'correlations',
            params: { novelId, correlationGroupId: item.correlationGroupId },
          }"
          :isActive="item.correlationGroupId === selectedCorrelationGroupId"
          :showHandle="extra"
          :item="item"
          :isEditMode="isEditing"
          disabledFolder
          @delete="deleteCorrelationGroup(item.correlationGroupId)"
        ></list-item>
      </template>
    </sortable-list>
    <button class="create" @click="createCorrelationGroup">{{ $t('correlationsPage.correlationGroupList.createButton') }}</button>
  </div>
</template>
<script lang="ts">
import Vue from "vue";
import ListItem from "@/components/molecules/ListItem.vue";
import SortableList from "@/components/molecules/SortableList.vue";
import { callApi } from "@/lib/api";
import { Plan } from "@/lib/models";
import { Dialog } from "@/lib/utils";
import BreakingChangeConfirm, { BreakingChangeConfirmProps } from "@/components/ui/dialogs/BreakingChangeConfirm.vue";
// NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
// import { Dialog } from "@/lib/utils";
// import PremiumFeatureDialog, { PremiumFeatureDialogProps } from "@/components/ui/PremiumFeatureDialog.vue";
// import { downloadPublicImage } from "@/lib/s3";

interface Data {
  isEditing: boolean;
  correlationGroupList: unknown[];
}

interface Methods {
  createCorrelationGroup: () => Promise<void>;
  deleteCorrelationGroup: (id: string) => Promise<void>;
  saveOrder: () => Promise<void>;
}

interface Props {
  novelId: string;
  items: unknown[];
  selectedCorrelationGroupId: string;
}

type CorrelationGroup = {
  correlationGroupId: string;
};

export default Vue.extend<Data, Methods, unknown, Props>({
  components: { ListItem, SortableList },
  props: {
    novelId: String,
    items: Array as any,
    selectedCorrelationGroupId: String,
  },
  data() {
    return {
      isEditing: false,
      correlationGroupList: this.items,
    };
  },
  watch: {
    items() {
      this.correlationGroupList = this.items;
    },
  },
  computed: {
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFree() {
      return this.plan === Plan.free;
    },
  },
  methods: {
    async createCorrelationGroup() {
      if (this.isFree && this.correlationGroupList.length) {
        // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
        // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
        // const data: PremiumFeatureDialogProps = {
        //   title: "相関図の複数作成はプレミアム機能です",
        //   text: "Nolaプレミアムにご登録いただくと、１つの作品内に２つ以上の『相関図の作成』を行うことができます。”各場面ごと”の相関図の作成や”〇〇高校”などの組織ごとに相関図を作成し資料管理することができますので、お手すきの際にぜひご活用いただだけたら幸いです。",
        //   image: (await downloadPublicImage("shared/images/gif/correlation_group.gif")) as string,
        // };
        // premiumFeatureDialog.show(data);

        this.$router.push({ name: "subscriptionAnnounce", query: { from: "createCorrelationGroup" } });

        return;
      }

      const { novelId } = this;
      const query = `
      mutation CreateCorrelationGroup ($input: CorrelationGroupCreateInput!) {
        createCorrelationGroup (input: $input) {
          novelId
          correlationGroupId
          name
          diagram {
            correlationKeys
            coordinate {
              characterId
              x
              y
            }
          }
        }
      }
      `;
      await callApi(query, {
        input: {
          novelId,
          name: this.$t('correlationsPage.correlationGroupList.newGroupName') as string,
        },
      });

      this.$emit("fetch");
    },
    async deleteCorrelationGroup(correlationGroupId) {
      const confirmDialog = new Dialog(BreakingChangeConfirm);
      const data: BreakingChangeConfirmProps = {
        title: this.$t('correlationsPage.correlationGroupList.deleteConfirmTitle') as string,
        content: this.$t('correlationsPage.correlationGroupList.deleteConfirmContent') as string,
        change: this.$t('correlationsPage.correlationGroupList.deleteButton') as string,
      };
      const result = await confirmDialog.show(data);

      if (!result) {
        return;
      }

      const { novelId } = this;
      const query = `
      mutation DeleteCorrelationGroup ($input: CorrelationGroupDeleteInput!) {
        deleteCorrelationGroup (input: $input) {
          novelId
          correlationGroupId
          name
          diagram {
            correlationKeys
            coordinate {
              characterId
              x
              y
            }
          }
        }
      }
      `;
      await callApi(query, {
        input: {
          novelId,
          correlationGroupId,
        },
      });

      this.$notify({
        title: this.$t('correlationsPage.correlationGroupList.deleted'),
        type: "error",
      });

      this.$router.push({
        name: "correlations",
        params: { novelId },
      });
    },
    async saveOrder() {
      const { novelId, correlationGroupList } = this;
      const order = correlationGroupList.map((item) => (item as CorrelationGroup).correlationGroupId);

      const query = `
        mutation UpdateDisplayOrder($input: UpdateDisplayOrderInput!) {
          updateDisplayOrder(input: $input) {
            order
          }
        }
      `;

      await callApi(query, {
        input: {
          novelId,
          kind: "correlationGroup",
          order,
        },
      });

      this.isEditing = false;
      this.$emit("fetch");
    },
  },
});
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";

.list-root {
  display: flex;
  flex-direction: column;
  height: 100%;

  @include mq(sp) {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .head {
    position: sticky;
    top: 0;

    display: flex;
    justify-content: space-between;
    color: var(--color-text-normal);
    font-weight: var(--font-weight-bold);
    background-color: var(--color-surface-subtle);

    .title {
      height: 95%;
      padding: 10px 15px;
      box-sizing: border-box;
      width: 100%;
    }

    .edit {
      display: flex;
      align-items: center;
      margin-right: 5px;
      color: var(--color-text-low);
      cursor: pointer;

      div {
        min-width: 55px;
        text-align: right;
      }
    }
  }

  .list-container {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  .create {
    position: relative;
    flex-shrink: 0;
    height: 50px;
    font-size: 1.2em;
    font-weight: var(--font-weight-bold);
    color: var(--color-text-on-fixed-bg);
    background-color: var(--color-primary);
    border: none;

    @include hover-overlay;

    @include mq(sp) {
      border-radius: 0;
      margin: 0;
    }
  }
}
</style>
